import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useBackTo } from '../../app/nav/originNav'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'
import PageHeader from '../../components/ui/PageHeader'
import EmptyState from '../../components/ui/EmptyState'
import LoadingSkeleton from '../../components/ui/LoadingSkeleton'
import HistoryTimeline, { type TimelineEvent } from '../../components/ui/HistoryTimeline'
import { useToast } from '../../components/ui/ToastProvider'
import { useTicketRequestDetail } from './hooks/useTicketRequestDetail'
import { useTicketLookups } from './hooks/useTicketLookups'
import { useApproveReject } from './hooks/useApproveReject'
import DetailCard from './components/DetailCard'
import ConfirmCard from './components/ConfirmCard'
import RejectReasonDialog from './components/RejectReasonDialog'
import { ticketActivityEvents } from './lib/ticketActivityEvents'
import { useGrants } from '../../auth/useCurrentUser'
import { GRANTS } from '../../auth/grants'
import { TICKET_REQUEST_STATUS } from './types'
import './tickets.css'

/**
 * Bilet Talep **Detay** ekranı (021: Figma detay tasarımına uyarlandı). Üstte admin info banner + başlık;
 * solda Talep Detayı bilgi kartı; sağ kolonda **Karar** (Onayla/Reddet) ve **İşlem Geçmişi**. Onay/ret
 * liste akışıyla aynı (`useApproveReject` + `ConfirmCard`/`RejectReasonDialog`); başarıda detay/liste
 * sorguları tazelenir. Karar kartı yalnız aksiyon alınabilir durumda görünür (Beklemede; ya da girişte
 * reddedilmiş → yalnız Onayla).
 */
export default function TicketRequestDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // "Geri": origin varsa (geldiği liste) oraya, yoksa Talep Listesi'ne (liste filtreleri zaten kalıcı).
  const backTo = useBackTo('/tickets/list')
  const [params] = useSearchParams()
  const toast = useToast()
  const qc = useQueryClient()

  const id = useMemo(() => {
    const raw = params.get('id')
    if (raw == null || raw.trim() === '') return null
    const n = Number(raw)
    return Number.isInteger(n) && n > 0 ? n : null
  }, [params])

  // 037: özel kalem, listede tam kimlikle bulduğu kendi-dışı talebi açtığında aynı kanıtı taşır
  // (URL'den) — backend kendi-dışı erişimi yalnız bu tam eşleşmeyle açar (yoksa 404).
  const exactPnr = params.get('exactPnr') ?? undefined
  const exactName = params.get('exactName') ?? undefined

  const { data, isLoading, isError } = useTicketRequestDetail(id, exactPnr, exactName)
  const lookups = useTicketLookups()
  const { approve, reject } = useApproveReject()
  const { hasGrant } = useGrants()
  const notFound = id == null || isError

  const [approveOpen, setApproveOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)

  const events: TimelineEvent[] = useMemo(
    () => (data ? ticketActivityEvents(data, t) : []),
    [data, t],
  )

  // Onayla/Reddet yalnız TICKET_REQUEST_APPROVE yetkisi olan kullanıcıda (FR-016; asıl koruma backend'de).
  const canDecide = hasGrant(GRANTS.TICKET_REQUEST_APPROVE)
  // Statü geçişleri (liste ile aynı): Beklemede → Onayla+Reddet; girişte reddedilmiş (302 + işlem
  // zamanı yok) → yalnız Onayla; onaylanmış/sistemce reddedilmiş → karar yok.
  const statusId = data?.status?.id
  const canApprove =
    canDecide &&
    (statusId === TICKET_REQUEST_STATUS.PENDING ||
      (statusId === TICKET_REQUEST_STATUS.REJECTED && data?.approveOrRejectTime == null))
  const canReject = canDecide && statusId === TICKET_REQUEST_STATUS.PENDING
  const showDecision = !!data && (canApprove || canReject)

  const refreshDetail = () => {
    qc.invalidateQueries({ queryKey: ['ticket-request-detail'] })
  }

  const onApproveConfirm = () => {
    if (!data) return
    approve.mutate(
      { id: data.id, body: { modDate: data.modDate } },
      {
        onSuccess: () =>
          toast.show({ severity: 'success', summary: t('tickets.approve.success'), life: 3000 }),
        onSettled: () => {
          setApproveOpen(false)
          refreshDetail()
        },
      },
    )
  }

  const onRejectConfirm = (rejectReasonId: number, rejectNote?: string) => {
    if (!data) return
    reject.mutate(
      { id: data.id, body: { rejectReasonId, rejectNote, modDate: data.modDate } },
      {
        onSuccess: () =>
          toast.show({ severity: 'success', summary: t('tickets.reject.success'), life: 3000 }),
        onSettled: () => {
          setRejectOpen(false)
          refreshDetail()
        },
      },
    )
  }

  return (
    <div className="page tickets-page tk-detail-page">
      <PageHeader
        title={t('tickets.detail.title')}
        actions={
          <Button
            variant="secondary"
            icon="pi pi-arrow-left"
            label={t('tickets.detail.back')}
            onClick={() => navigate(backTo)}
          />
        }
      />

      {isLoading && id != null && (
        <div className="tk-detail-layout" aria-busy>
          <Card className="tk-detail-card">
            <LoadingSkeleton lines={6} />
          </Card>
          <Card className="tk-timeline-card">
            <LoadingSkeleton lines={4} />
          </Card>
        </div>
      )}

      {notFound && (
        <EmptyState
          icon="pi pi-exclamation-circle"
          title={t('tickets.detail.notFound.title')}
          description={t('tickets.detail.notFound.desc')}
        />
      )}

      {!notFound && data && (
        <div className="tk-detail-layout">
          <DetailCard detail={data} />

          <div className="tk-detail-side">
            {showDecision && (
              <Card className="tk-decision-card">
                <h2 className="tk-timeline-title">{t('tickets.detail.decision')}</h2>
                <div className="tk-decision-acts">
                  {canApprove && (
                    <Button
                      icon="pi pi-check"
                      label={t('tickets.actions.approve')}
                      severity="success"
                      loading={approve.isPending}
                      onClick={() => setApproveOpen(true)}
                    />
                  )}
                  {canReject && (
                    <Button
                      icon="pi pi-times"
                      label={t('tickets.actions.reject')}
                      severity="danger"
                      outlined
                      loading={reject.isPending}
                      onClick={() => setRejectOpen(true)}
                    />
                  )}
                </div>
              </Card>
            )}

            <Card className="tk-timeline-card">
              <h2 className="tk-timeline-title">{t('tickets.detail.history.title')}</h2>
              <HistoryTimeline events={events} />
            </Card>
          </div>
        </div>
      )}

      <ConfirmCard
        visible={approveOpen}
        title={t('tickets.approve.title')}
        subtitle={data ? `${data.pnr} - ${data.nameSurname}` : undefined}
        message={data ? t('tickets.approve.confirm') : ''}
        confirmLabel={t('tickets.approve.accept')}
        tone="ok"
        submitting={approve.isPending}
        onConfirm={onApproveConfirm}
        onHide={() => setApproveOpen(false)}
      />

      <RejectReasonDialog
        visible={rejectOpen}
        reasons={lookups.data?.rejectReasons ?? []}
        submitting={reject.isPending}
        subtitle={data ? `${data.pnr} - ${data.nameSurname}` : undefined}
        onConfirm={onRejectConfirm}
        onHide={() => setRejectOpen(false)}
      />
    </div>
  )
}
